import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { colorPalette } from "../styles/theme";
import { PeriodControlBar, getQuickRange } from "./OverviewView";
import { PHQ_BANDS, GAD_BANDS, getBandLabel } from "./screeningBands";
import { useWsDataset, buildScreeningRecords, buildOrgDistressSeries } from "./wsData";
import OrgDistressAreaChart from "./OrgDistressAreaChart";

/**
 * 기관 통계 리포트 (샘플) — 기존 검사 통계 탭을 일상 마음 건강 + 임상 선별검사 종합 리포트로 확장
 * - 섹션 1: 기관 전체 주간/월간 마음 건강 추이 (영역형 시계열, 수치 비노출)
 * - 섹션 2: 임상 선별검사(PHQ-9 / GAD-7) 기간별 추이 및 분포
 *   · 기간별 평균 점수 버블-라인 차트 (기존 "검사 통계" 탭 차트) + 표준 임상 절단점 기준선(10점 중등도 / 15점 중증)
 *   · 검사 점수 통계 표 + "중등도 이상(10점↑) 인원(비율)" 컬럼
 * - PHQ-9 / GAD-7은 표준 임상 척도라 점수를 그대로 노출합니다. (일일 지표와 달리 IP 보호 대상 아님)
 *
 * WS 샘플: CSV 실데이터의 검사 기록과 일별 디스트레스를 집계하며, 차트와 표는 같은 기록에서 계산합니다.
 */

const DEFAULT_RANGE_DAYS = 30;
const MODERATE_CUTOFF = 10; // 중등도 이상 판정 기준 (PHQ-9 / GAD-7 공통)

// 검사별 설정 — 점수 범위, 색상, 그래프 배경 임상 절단점 기준선
const SCREENINGS = [
  {
    key: "phq",
    title: "PHQ-9 우울증 선별검사",
    chartTitle: "PHQ-9 평균 점수",
    maxScore: 27,
    color: colorPalette.primary,
    bands: PHQ_BANDS,
    cutoffs: [
      { score: 10, label: "중등도 기준선" },
      { score: 15, label: "중증 기준선" },
    ],
  },
  {
    key: "gad",
    title: "GAD-7 불안장애 선별검사",
    chartTitle: "GAD-7 평균 점수",
    maxScore: 21,
    color: "#f59e0b",
    bands: GAD_BANDS,
    cutoffs: [
      { score: 10, label: "중등도 기준선" },
      { score: 15, label: "중증 기준선" },
    ],
  },
];

// ─── 날짜 유틸 ─────────────────────────────────────────────────────
const pad = (n) => String(n).padStart(2, "0");
const toDateString = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const getDateList = (start, end) => {
  const [sy, sm, sd] = start.split("-").map(Number);
  const [ey, em, ed] = end.split("-").map(Number);
  const dates = [];
  for (let d = new Date(sy, sm - 1, sd); d <= new Date(ey, em - 1, ed); d.setDate(d.getDate() + 1)) {
    dates.push(toDateString(d));
  }
  return dates;
};

// ─── 데이터 집계 ───────────────────────────────────────────────────
/** 날짜별 평균 점수·검사 건수 (버블-라인 차트용) — 검사가 없는 날도 x축에 남도록 avg: null로 채움 */
const buildTrend = (records, range) => {
  const byDate = new Map();
  records.forEach(({ date, score }) => {
    const entry = byDate.get(date) ?? { total: 0, count: 0 };
    entry.total += score;
    entry.count += 1;
    byDate.set(date, entry);
  });
  return getDateList(range.start, range.end).map((date) => {
    const entry = byDate.get(date);
    return entry
      ? { date, avg: Math.round((entry.total / entry.count) * 10) / 10, count: entry.count }
      : { date, avg: null, count: 0 };
  });
};

/** 검사 점수 통계 표 1행 — 중등도 이상은 "기간 내 수검자 중 최근 점수가 10점 이상인 인원" 기준 */
const buildScoreRow = (records, screening) => {
  if (records.length === 0) {
    return { ...screening, count: 0, average: null, max: null, min: null, moderatePeople: 0, testedPeople: 0 };
  }
  const scores = records.map((r) => r.score);
  const latestByUser = new Map();
  [...records].sort((a, b) => (a.date < b.date ? -1 : 1)).forEach((r) => latestByUser.set(r.userId, r.score));
  const latestScores = [...latestByUser.values()];

  return {
    ...screening,
    count: records.length,
    average: Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 10) / 10,
    max: Math.max(...scores),
    min: Math.min(...scores),
    moderatePeople: latestScores.filter((score) => score >= MODERATE_CUTOFF).length,
    testedPeople: latestScores.length,
  };
};

// ─── 기간별 평균 점수 버블-라인 차트 ─────────────────────────────────
// 날짜 라벨이 겹치지 않도록 데이터 포인트 하나당 확보할 최소 가로 간격(px)
const MIN_X_STEP = 26;
const MIN_CHART_WIDTH = 560;
const CUTOFF_COLOR = "#dc2626";

/**
 * 기존 검사 통계 탭의 TrendLineChart(순수 SVG)를 바탕으로 임상 절단점 기준선을 추가한 차트
 * - x축: 검사 날짜 / y축: 그날 평균 점수(0~만점) / 점 크기: 그날 검사 건수
 * - 배경: 중등도·중증 기준선(연한 점선 + 라벨), 기준선 위 영역을 옅게 칠해 임상 위치를 바로 보이게 함
 */
const ScreeningTrendChart = ({ data, color, maxY, cutoffs, bands }) => {
  const height = 240;
  const padding = { top: 16, right: 96, bottom: 32, left: 36 };
  const [hoveredIndex, setHoveredIndex] = useState(null);

  if (!data.some((d) => typeof d.avg === "number")) {
    return (
      <Box
        sx={{
          py: 5,
          textAlign: "center",
          border: `1px dashed ${colorPalette.border}`,
          borderRadius: 1,
          color: colorPalette.textSecondary,
        }}
      >
        <Typography variant="body2">조회 기간에 검사 기록이 없습니다.</Typography>
      </Box>
    );
  }

  const width = Math.max(MIN_CHART_WIDTH, padding.left + padding.right + (data.length - 1) * MIN_X_STEP);
  const innerWidth = width - padding.left - padding.right;
  const innerHeight = height - padding.top - padding.bottom;
  const xStep = data.length > 1 ? innerWidth / (data.length - 1) : 0;
  const xScale = (index) => padding.left + xStep * index;
  const yScale = (value) => padding.top + innerHeight - (value / maxY) * innerHeight;

  const maxCount = Math.max(...data.map((d) => d.count), 0);
  // 검사 횟수가 많을수록 점이 커지도록 반지름을 6~16px 범위로 스케일링 (기존과 동일)
  const radiusFor = (count) => (maxCount <= 1 ? 8 : 6 + (count / maxCount) * 10);

  // 값이 없는 날짜는 점을 찍지 않고, 실제 값이 있는 지점끼리 바로 이어줌 (기존과 동일)
  const linePath = data
    .map((d, i) => (typeof d.avg === "number" ? { x: xScale(i), y: yScale(d.avg) } : null))
    .filter(Boolean)
    .map((p, k) => `${k === 0 ? "M" : "L"} ${p.x} ${p.y}`)
    .join(" ");

  const yTicks = Array.from({ length: Math.floor(maxY / 5) + 1 }, (_, i) => i * 5);
  // 날짜 라벨은 약 48px당 1개만 표시
  const labelEvery = Math.max(1, Math.ceil(48 / Math.max(xStep, 1)));
  const lowestCutoff = Math.min(...cutoffs.map((c) => c.score));
  const hovered = hoveredIndex != null ? data[hoveredIndex] : null;

  return (
    <Box sx={{ position: "relative", overflowX: "auto" }}>
      <svg width={width} height={height} role="img" style={{ display: "block" }}>
        {/* 중등도 기준선 위 영역을 옅게 칠해 임상적으로 주의가 필요한 구간을 구분 */}
        <rect
          x={padding.left}
          y={padding.top}
          width={innerWidth}
          height={yScale(lowestCutoff) - padding.top}
          fill={CUTOFF_COLOR}
          fillOpacity="0.04"
        />

        {/* y축 그리드 + 점수 라벨 (표준 임상 척도라 점수 그대로 노출) */}
        {yTicks.map((tick) => (
          <g key={tick}>
            <line
              x1={padding.left}
              x2={width - padding.right}
              y1={yScale(tick)}
              y2={yScale(tick)}
              stroke={colorPalette.border}
              strokeOpacity={tick === 0 ? 1 : 0.6}
              strokeDasharray={tick === 0 ? undefined : "2 4"}
            />
            <text x={padding.left - 6} y={yScale(tick) + 3} textAnchor="end" fontSize="10" fill={colorPalette.textSecondary}>
              {tick}
            </text>
          </g>
        ))}

        {/* 임상 절단점 기준선 (연한 점선 + 우측 라벨) */}
        {cutoffs.map((cutoff) => (
          <g key={cutoff.score}>
            <line
              x1={padding.left}
              x2={width - padding.right}
              y1={yScale(cutoff.score)}
              y2={yScale(cutoff.score)}
              stroke={CUTOFF_COLOR}
              strokeOpacity="0.45"
              strokeWidth="1.25"
              strokeDasharray="6 4"
            />
            <text
              x={width - padding.right + 8}
              y={yScale(cutoff.score) + 3}
              textAnchor="start"
              fontSize="10"
              fontWeight="700"
              fill={CUTOFF_COLOR}
              fillOpacity="0.8"
            >
              {cutoff.label} {cutoff.score}점
            </text>
          </g>
        ))}

        {/* 평균 점수 추이선 */}
        <path d={linePath} fill="none" stroke={color} strokeWidth="2" />

        {/* 데이터 포인트 (원 크기 = 검사 횟수) + x축 날짜 라벨 */}
        {data.map((d, i) => (
          <g key={d.date}>
            {typeof d.avg === "number" && (
              <>
                {/* 실제 원보다 넉넉한 투명 히트 영역 — 원이 작아도 마우스오버가 잘 걸리도록 함 */}
                <circle
                  cx={xScale(i)}
                  cy={yScale(d.avg)}
                  r={Math.max(radiusFor(d.count), 12)}
                  fill="transparent"
                  onMouseEnter={() => setHoveredIndex(i)}
                  onMouseLeave={() => setHoveredIndex((prev) => (prev === i ? null : prev))}
                  style={{ cursor: "pointer" }}
                />
                <circle
                  cx={xScale(i)}
                  cy={yScale(d.avg)}
                  r={radiusFor(d.count)}
                  fill={color}
                  fillOpacity={hoveredIndex === i ? 0.95 : 0.75}
                  stroke="#fff"
                  strokeWidth={hoveredIndex === i ? 2.5 : 1.5}
                  style={{ pointerEvents: "none", transition: "fill-opacity 0.1s ease" }}
                />
              </>
            )}
            {i % labelEvery === 0 && (
              <text x={xScale(i)} y={height - padding.bottom + 16} textAnchor="middle" fontSize="10" fill={colorPalette.textSecondary}>
                {d.date.slice(5)}
              </text>
            )}
          </g>
        ))}
      </svg>

      {/* 툴팁 — 평균 점수와 함께 임상 해석 구간을 표시 */}
      {hovered && (() => {
        const tooltipLeft = xScale(hoveredIndex);
        const tooltipTop = yScale(hovered.avg);
        const alignX = tooltipLeft < 90 ? "start" : tooltipLeft > width - 140 ? "end" : "center";
        const translateX = alignX === "start" ? "0%" : alignX === "end" ? "-100%" : "-50%";
        const translateY = tooltipTop < 55 ? "20%" : "-130%";
        return (
          <Box
            sx={{
              position: "absolute",
              left: tooltipLeft,
              top: tooltipTop,
              transform: `translate(${translateX}, ${translateY})`,
              px: 1.2,
              py: 0.7,
              borderRadius: 1,
              backgroundColor: "#1a1f36",
              color: "#fff",
              fontSize: 11,
              fontWeight: 600,
              whiteSpace: "nowrap",
              pointerEvents: "none",
              boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
              zIndex: 1,
            }}
          >
            {hovered.date} · 평균 {hovered.avg}점({getBandLabel(bands, hovered.avg)}) · 검사 {hovered.count}건
          </Box>
        );
      })()}
    </Box>
  );
};

// ─── 검사 점수 통계 표 ─────────────────────────────────────────────
const TABLE_COLUMNS = "1fr 96px 96px 96px 96px 170px";
const TABLE_HEADERS = ["검사 종류", "검사 횟수", "평균 점수", "최고 점수", "최저 점수", "중등도 이상(10점↑) 인원"];

const cellSx = { textAlign: "center", color: colorPalette.textSecondary, fontSize: 13 };

/** 중등도 이상 인원 셀 — 인원(비율) + 비율 막대, 한 명이라도 있으면 붉은색 강조 */
const ModerateCell = ({ people, tested }) => {
  const ratio = tested ? Math.round((people / tested) * 100) : 0;
  const alert = people > 0;
  return (
    <Box sx={{ px: 1 }}>
      <Typography sx={{ textAlign: "center", fontSize: 13, fontWeight: 700, color: alert ? "#dc2626" : colorPalette.textSecondary }}>
        {people}명{" "}
        <Box component="span" sx={{ fontWeight: 500 }}>
          ({ratio}%)
        </Box>
      </Typography>
      <Box sx={{ mt: 0.5, height: 4, borderRadius: 2, backgroundColor: colorPalette.secondary, overflow: "hidden" }}>
        <Box sx={{ width: `${ratio}%`, height: "100%", backgroundColor: alert ? "#ef4444" : colorPalette.border }} />
      </Box>
      <Typography sx={{ mt: 0.25, textAlign: "center", fontSize: 10, color: colorPalette.textSecondary }}>
        수검자 {tested}명 중
      </Typography>
    </Box>
  );
};

const ScoreStatsTable = ({ rows }) => (
  <Box sx={{ overflowX: "auto" }}>
    <Box sx={{ minWidth: 720 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: TABLE_COLUMNS,
          alignItems: "center",
          px: 2,
          py: 1.5,
          borderRadius: 1,
          backgroundColor: colorPalette.background,
          border: `1px solid ${colorPalette.border}`,
        }}
      >
        {TABLE_HEADERS.map((label) => (
          <Typography key={label} variant="caption" sx={{ color: colorPalette.textSecondary, fontWeight: 700, textAlign: "center" }}>
            {label}
          </Typography>
        ))}
      </Box>
      <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1 }}>
        {rows.map((row) => (
          <Box
            key={row.key}
            sx={{
              display: "grid",
              gridTemplateColumns: TABLE_COLUMNS,
              alignItems: "center",
              px: 2,
              py: 1.5,
              border: `1px solid ${colorPalette.border}`,
              borderRadius: 1,
              backgroundColor: colorPalette.white,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: colorPalette.textPrimary, fontSize: 13 }}>
              {row.title}
            </Typography>
            <Typography sx={cellSx}>{row.count}회</Typography>
            <Typography sx={cellSx}>{row.average ?? "-"}</Typography>
            <Typography sx={cellSx}>{row.max ?? "-"}</Typography>
            <Typography sx={cellSx}>{row.min ?? "-"}</Typography>
            <ModerateCell people={row.moderatePeople} tested={row.testedPeople} />
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
);

// ─── 섹션 공통 틀 ──────────────────────────────────────────────────
const ReportSection = ({ number, title, description, children }) => (
  <Paper variant="outlined" sx={{ p: 3, mb: 3, backgroundColor: colorPalette.white }}>
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 800, color: colorPalette.primary }}>섹션 {number}</Typography>
      <Typography sx={{ fontSize: 16, fontWeight: 700, color: colorPalette.textPrimary }}>{title}</Typography>
    </Box>
    {description && (
      <Typography sx={{ mt: 0.5, mb: 2.5, fontSize: 12, color: colorPalette.textSecondary }}>{description}</Typography>
    )}
    {children}
  </Paper>
);

/**
 * @param {string} scopeKey - 조회 범위 키 ("ALL" = 전체 기관, 그 외 사업 code)
 */
const ReportView = ({ scopeKey }) => {
  const dataset = useWsDataset();
  const [appliedRange, setAppliedRange] = useState(() => getQuickRange(DEFAULT_RANGE_DAYS));

  const screeningStats = useMemo(
    () =>
      SCREENINGS.map((screening) => {
        const records = buildScreeningRecords(dataset, scopeKey, appliedRange.start, appliedRange.end, screening.key);
        return { screening, trend: buildTrend(records, appliedRange), row: buildScoreRow(records, screening) };
      }),
    [dataset, scopeKey, appliedRange],
  );
  const orgDistressSeries = useMemo(
    () => buildOrgDistressSeries(dataset, scopeKey, appliedRange.start, appliedRange.end),
    [dataset, scopeKey, appliedRange],
  );

  return (
    <Box sx={{ flex: 1, overflowY: "auto", p: 4 }}>
      <PeriodControlBar appliedRange={appliedRange} onApply={setAppliedRange} />

      <ReportSection
        number={1}
        title="기관 전체 주간/월간 마음 건강 추이"
        description="선택한 기간 동안 기관 전체 참가자의 평균 디스트레스 흐름을 지역 평균 기준선과 비교합니다. 수치 대신 4단계 구간(낮음~매우 높음)으로 표시하며, 일별/주별로 묶어 볼 수 있습니다."
      >
        <OrgDistressAreaChart series={orgDistressSeries} />
      </ReportSection>

      <ReportSection
        number={2}
        title="임상 선별검사(PHQ-9 / GAD-7) 기간별 추이 및 분포"
        description="점 크기는 해당 날짜의 검사 횟수에 비례합니다. 붉은 점선은 표준 임상 절단점(10점 중등도 / 15점 중증)으로, 기관 평균이 임상적으로 어느 수준인지 보여 줍니다."
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mb: 4 }}>
          {screeningStats.map(({ screening, trend }) => (
            <Paper key={screening.key} variant="outlined" sx={{ p: 2, backgroundColor: colorPalette.white }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: colorPalette.textPrimary, fontSize: 13 }}>
                {screening.chartTitle}{" "}
                <Box component="span" sx={{ fontWeight: 400, color: colorPalette.textSecondary }}>
                  (0~{screening.maxScore}점)
                </Box>
              </Typography>
              <ScreeningTrendChart
                data={trend}
                color={screening.color}
                maxY={screening.maxScore}
                cutoffs={screening.cutoffs}
                bands={screening.bands}
              />
            </Paper>
          ))}
        </Box>

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: colorPalette.textPrimary }}>
          검사 점수 통계
        </Typography>
        <Typography variant="caption" sx={{ display: "block", mb: 1.5, color: colorPalette.textSecondary }}>
          중등도 이상 인원은 기간 내 검사를 받은 참가자 중 가장 최근 점수가 10점 이상인 인원과 그 비율입니다.
        </Typography>
        <ScoreStatsTable rows={screeningStats.map(({ row }) => row)} />
      </ReportSection>
    </Box>
  );
};

export default ReportView;
